import { useCallback, useMemo } from 'react';
import { useUserContext } from '@blocs.auth';
import { captureHeapEvent, HEAP_EVENT_NAMES } from '@blocs.heap';
import { useTranslation } from '@blocs.i18n';
import { PaywallLocation, PurchaseResultType, useBillingPopup } from '@blocs.paywall';
import { useRouteHelper } from '@consumer-talent.utils/routeHelper';
import { useArtistContext } from '../core/ArtistContext';

export type UseHeadshotTrialModalParams = {
  /** Closes the open headshots modal; owned by `useHeadshotModal`. */
  onClose: () => void;
};

/**
 * Paid-trial headshots upsell: the formatted trial end date and the
 * upgrade callback. `useHeadshotModal` owns which modal is open.
 */
export const useHeadshotTrialModal = ({ onClose }: UseHeadshotTrialModalParams) => {
  const { myAccount } = useArtistContext();
  const { subscription } = useUserContext();
  const { formatIntlDate } = useTranslation();
  const billingPopup = useBillingPopup();
  const { push } = useRouteHelper();

  const trialEndDate = formatIntlDate({
    date: subscription?.trialEndsAt,
    format: 'MONTH_DAY_YEAR',
    timeZoneStandardName: myAccount?.timeZone?.standardName,
  });

  const handleTrialUpgrade = useCallback(async () => {
    captureHeapEvent({
      name: HEAP_EVENT_NAMES.TALENT_CLICK_HEADSHOTS_TRIAL_UPGRADE_MODULE_UPGRADE_NOW,
    });
    onClose();

    const { getPurchaseResult } = billingPopup.show({
      location: PaywallLocation.HEADSHOTS_TRIAL,
    });

    // A paused subscription opens the resume modal and does not return a purchase promise.
    if (!getPurchaseResult) {
      return;
    }

    // This await lives on the nav, which stays mounted after the trial modal closes.
    // BillingPopupProvider resolves the promise when the paywall closes. It rejects
    // with "Component unmounted" only if that provider itself unmounts first.
    const purchaseResult = await getPurchaseResult();

    if (purchaseResult.type === PurchaseResultType.Success) {
      push('headshots');
    }
  }, [billingPopup, onClose, push]);

  return useMemo(
    () => ({
      trialEndDate,
      handleTrialUpgrade,
    }),
    [handleTrialUpgrade, trialEndDate]
  );
};

export type UseHeadshotTrialModalValues = ReturnType<typeof useHeadshotTrialModal>;
